import type { Component } from 'vue';
import {
  ChartColumn, Bug, Swords, Shuffle, ShieldCheck, MessageSquare, Ban, Users, Flag, Ticket,
  Warehouse, Cpu, Package, Gift, ShoppingCart, Coins, Target, Trophy, Newspaper, Map,
  PenTool, SlidersHorizontal, Database, Settings, UserCog, History, Film, Bot, CalendarRange, Building2, Crosshair,
  ServerCog, Palette, MessagesSquare, Inbox, Radio,
} from '@lucide/vue';

export interface NavLink {
  to: string;
  label: string;
  /** perm === `${route-name}:read` (кроме garage/kits/devices — те под items, см. router.ts). */
  perm: string;
  icon: Component;
  /** Доп. слова для поиска в палитре команд. */
  keywords?: string;
}

export interface NavGroup {
  label: string;
  links: NavLink[];
}

/**
 * Разделы панели, сгруппированные по смыслу. Единственный источник для сайдбара и палитры
 * команд (Ctrl+K). При добавлении раздела не забыть router.ts (маршрут + TAB_ORDER) и
 * AdminPermission.TABS на сервере.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    links: [
      { to: '/stats', label: 'Stats', perm: 'stats:read', icon: ChartColumn, keywords: 'dashboard online cpu metrics restart' },
      { to: '/debug', label: 'Debug', perm: 'debug:read', icon: Bug, keywords: 'spaces clients server state' },
    ],
  },
  {
    label: 'Live operations',
    links: [
      { to: '/battles', label: 'Battles', perm: 'battles:read', icon: Swords },
      { to: '/battle-royale', label: 'Battle Royale', perm: 'battle-royale:read', icon: Crosshair, keywords: 'br loot inventory zone airdrop bots royale' },
      { to: '/replays', label: 'Replays', perm: 'replays:read', icon: Film, keywords: 'recordings matches export training' },
      { to: '/matchmaking', label: 'Matchmaking', perm: 'matchmaking:read', icon: Shuffle, keywords: 'queue gear score' },
      { to: '/bots', label: 'Bots', perm: 'bots:read', icon: Bot, keywords: 'ai difficulty matchmaking fill clan tag' },
      { to: '/moderator', label: 'Moderator', perm: 'moderator:read', icon: ShieldCheck, keywords: 'reports vote console' },
      { to: '/chat', label: 'Chat', perm: 'chat:read', icon: MessageSquare, keywords: 'messages channels filter spam' },
      { to: '/bans', label: 'Bans', perm: 'bans:read', icon: Ban, keywords: 'ip account block' },
    ],
  },
  {
    label: 'Community',
    links: [
      { to: '/players', label: 'Players', perm: 'players:read', icon: Users, keywords: 'users accounts profile items' },
      { to: '/account-requests', label: 'Account requests', perm: 'account-requests:read', icon: Inbox, keywords: 'password restore forgot e-mail email confirm recovery link' },
      { to: '/player-roles', label: 'Player roles', perm: 'players:read', icon: UserCog, keywords: 'moderator admin spectator replay viewer privileges chat commands ban mute permissions' },
      { to: '/clans', label: 'Clans', perm: 'clans:read', icon: Flag },
      { to: '/invites', label: 'Invites', perm: 'invites:read', icon: Ticket, keywords: 'codes' },
      { to: '/news', label: 'News', perm: 'news:read', icon: Newspaper },
      { to: '/forum', label: 'Forum', perm: 'forum:read', icon: MessagesSquare, keywords: 'ips invision categories sections boards subforums' },
    ],
  },
  {
    label: 'Content & economy',
    links: [
      { to: '/garage', label: 'Garage', perm: 'items:read', icon: Warehouse, keywords: 'items hulls weapons turrets paints skins mk' },
      { to: '/garage-halls', label: 'Garage Halls', perm: 'items:read', icon: Building2, keywords: 'hall hangar garage box beta tester forced for everyone' },
      { to: '/devices', label: 'Devices', perm: 'items:read', icon: Cpu, keywords: 'augments' },
      { to: '/kits', label: 'Kits', perm: 'items:read', icon: Package, keywords: 'bundles' },
      { to: '/containers', label: 'Containers', perm: 'containers:read', icon: Gift, keywords: 'loot boxes drops simulator' },
      { to: '/shop', label: 'Shop', perm: 'shop:read', icon: ShoppingCart, keywords: 'store payments crystals' },
      { to: '/streamers', label: 'Streamers', perm: 'streamers:read', icon: Radio, keywords: 'streamer partner affiliate code promo creator twitch youtube share discount' },
      { to: '/economy', label: 'Economy', perm: 'economy:read', icon: Coins, keywords: 'fund score multipliers presets' },
      { to: '/events', label: 'Events', perm: 'events:read', icon: CalendarRange, keywords: 'halloween holiday seasonal discounts skulls special missions sale' },
      { to: '/quests', label: 'Missions', perm: 'quests:read', icon: Target, keywords: 'quests daily weekly' },
      { to: '/challenges', label: 'Challenges', perm: 'challenges:read', icon: Trophy, keywords: 'stars battle pass' },
    ],
  },
  {
    label: 'World',
    links: [
      { to: '/maps', label: 'Maps', perm: 'maps:read', icon: Map },
      { to: '/map-editor', label: 'Map Editor', perm: 'map-editor:read', icon: PenTool, keywords: 'spawns flags zones 3d' },
      { to: '/map-presets', label: 'Map Presets', perm: 'map-presets:read', icon: SlidersHorizontal, keywords: 'skybox fog dust shadow ssao' },
      { to: '/resources', label: 'Resources', perm: 'resources:read', icon: Database, keywords: 'cdn textures' },
    ],
  },
  {
    label: 'System',
    links: [
      { to: '/appearance', label: 'Appearance', perm: 'appearance:read', icon: Palette, keywords: 'interface theme skin ui halloween classic 2.0 look seasonal' },
      { to: '/global-settings', label: 'Global Settings', perm: 'global-settings:read', icon: Settings, keywords: 'gifts respawn timings anticheat cache emotes emoji' },
      { to: '/servers', label: 'Servers', perm: 'servers:read', icon: ServerCog, keywords: 'stack cluster nodes regions ping handoff multiregion' },
      { to: '/users', label: 'Admin Users', perm: 'users:read', icon: UserCog, keywords: 'roles permissions staff' },
      { to: '/admin-activity', label: 'Admin Activity', perm: 'admin-activity:read', icon: History, keywords: 'audit log journal history revert undo rollback' },
    ],
  },
];

export const ALL_LINKS: NavLink[] = NAV_GROUPS.flatMap((g) => g.links);

export function findLink(path: string): NavLink | undefined {
  return ALL_LINKS.find((l) => path === l.to || path.startsWith(l.to + '/'));
}
